import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../api.ts'

/**
 * "Zmień hasło" for the logged-in person. The first password every account
 * gets is a generated one-time string — this is where it becomes something
 * you can actually remember. Other sessions of the user are dropped by the
 * server; this one stays.
 */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (next.length < 8) return setError('Nowe hasło musi mieć co najmniej 8 znaków.')
    if (next !== again) return setError('Nowe hasło i powtórzenie różnią się.')
    if (next === current) return setError('Nowe hasło musi być inne niż obecne.')
    setBusy(true)
    try {
      await api.post('/api/me/haslo', { current, next })
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zmienić hasła')
    } finally {
      setBusy(false)
    }
  }

  const INPUT = 'w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600'

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-24" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-slate-200 p-5 space-y-3"
      >
        <h2 className="font-bold text-slate-800">Zmień hasło</h2>
        {done ? (
          <>
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              Hasło zmienione. Od teraz logujesz się nowym — na innych urządzeniach trzeba zalogować się ponownie.
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700">
                Zamknij
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="block text-xs text-slate-500">
              Obecne hasło
              <input type="password" autoComplete="current-password" autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} className={`${INPUT} mt-0.5`} />
            </label>
            <label className="block text-xs text-slate-500">
              Nowe hasło <span className="text-slate-400">(min. 8 znaków)</span>
              <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={`${INPUT} mt-0.5`} />
            </label>
            <label className="block text-xs text-slate-500">
              Powtórz nowe hasło
              <input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} className={`${INPUT} mt-0.5`} />
            </label>
            {error && (
              <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="text-sm text-slate-500 px-3 py-2 hover:text-slate-700">
                Anuluj
              </button>
              <button type="submit" disabled={busy || !current || !next || !again} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {busy ? 'Zapisuję…' : 'Zmień hasło'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
